import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel } from "../i18n";
import CustomDropdown from "./CustomDropdown";
import { toogleErrorMessage, toogleSuccessMessage } from "../actions/alertMessage";
import { countryCodes } from "../data/countryCodes";
import { isValidEmailAddress } from "../utils/formValidation";
import {
  handleNumericInputKeyDown,
  sanitizeNumericInput,
} from "../utils/numericInput";
import "./CreateQuimeraOrderModal.css";

const isPlaceholder = (value) => !value || String(value).trim() === "" || String(value).trim() === "—";
const usableText = (value) => (isPlaceholder(value) ? "" : String(value).trim());
const getQuantityAmount = (value) => {
  const match = String(value || "").match(/[0-9]+(?:[,.][0-9]+)*/);
  return match ? match[0].replace(/,/g, "") : "";
};
const localDateInput = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const PHONE_COUNTRY_OPTIONS = countryCodes
  .filter((country) => country?.name && country?.dial_code && country?.code)
  .map((country) => ({
    value: country.code,
    label: country.dial_code,
    selectedLabel: country.dial_code,
    flagImageUrl: "https://flagcdn.com/24x18/" + country.code.toLowerCase() + ".png",
    description: country.name,
    searchText: country.name + " " + country.dial_code + " " + country.code,
  }))
  .sort((a, b) => a.description.localeCompare(b.description));

const renderPhoneCountryLabel = (country) => (
  <span className="phone-country-option-label">
    <img
      className="phone-country-flag"
      src={country.flagImageUrl}
      alt={country.value}
      loading="lazy"
    />
    <span>{country.label}</span>
  </span>
);

const LOGISTICS_TYPES = [
  { value: "air", label: "Air freight", icon: "M3 12.5 21 4l-5.8 16-3.2-6.4L3 12.5Zm9 1.1L21 4" },
  { value: "sea", label: "Sea freight", icon: "M4 18h16M6 18l-2 3m6-3-1 3m5-3v3m3-3 2 3M4 14h16l-2 4H6l-2-4Zm4-3V5h8v6m-4-6V3" },
  { value: "rail", label: "Rail freight", icon: "M7 3h10a2 2 0 0 1 2 2v12H5V5a2 2 0 0 1 2-2Zm-2 10h14M8 17l-2 4m10-4 2 4M8 7h.01M16 7h.01" },
  { value: "road", label: "Road transport", icon: "M3 6h11v12H3zM14 10h4l3 3v5h-7zM7 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" },
];

const getCountry = (inquiry) => {
  const countryName = usableText(inquiry?.country).toLowerCase();
  const phone = usableText(inquiry?.sellerPhone);
  return (
    countryCodes.find((country) => country.name.toLowerCase() === countryName) ||
    countryCodes.find((country) => phone.startsWith(country.dial_code)) ||
    countryCodes.find((country) => country.code === "IN") ||
    countryCodes[0]
  );
};

const initialForm = (inquiry, country) => {
  const initialPhone = usableText(inquiry?.sellerPhone);
  const initialImage = usableText(inquiry?.image);
  const phoneNumber = initialPhone.startsWith(country.dial_code)
    ? initialPhone.slice(country.dial_code.length).trim()
    : initialPhone;
  return {
    logisticsType: "",
    pickupAddress: usableText(inquiry?.portOfLoading),
    pickupAddressDetails: "",
    dropAddress: usableText(inquiry?.deliveryLocation) || usableText(inquiry?.destinationPortLocation) || usableText(inquiry?.portOfDischarge),
    dropAddressDetails: "",
    countryCode: country.code,
    phoneNumber: sanitizeNumericInput(phoneNumber, { allowDecimal: false }),
    contactName: usableText(inquiry?.seller),
    pickupDate: localDateInput(),
    email: usableText(inquiry?.sellerEmail),
    itemName: usableText(inquiry?.commodity),
    quantity: getQuantityAmount(inquiry?.quantity),
    weightKg: "",
    heightCm: "",
    widthCm: "",
    detailedRequirements: usableText(inquiry?.message),
    productImage:
      initialImage === "/assets/inquiries-icon.svg" ? "" : initialImage,
  };
};

const mapEmbedUrl = (query) =>
  "https://maps.google.com/maps?q=" + encodeURIComponent(query || "World") + "&output=embed";

const GOOGLE_MAPS_API_KEY = String(
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
)
  .trim()
  .replace(/^['"]|['"]$/g, "");
let googleMapsScriptPromise;
const GOOGLE_MAPS_CALLBACK = "__lualiQuimeraMapsReady";

const loadGoogleMapsApi = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps is only available in a browser."));
  }
  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(new Error("Configure REACT_APP_GOOGLE_MAPS_API_KEY to enable interactive maps."));
  }
  if (window.google?.maps?.importLibrary && window.google.maps.Map) {
    return Promise.resolve(window.google.maps);
  }
  if (!googleMapsScriptPromise) {
    googleMapsScriptPromise = new Promise((resolve, reject) => {
      let timeoutId;
      const script = document.createElement("script");
      const clearLoader = () => {
        window.clearTimeout(timeoutId);
        delete window[GOOGLE_MAPS_CALLBACK];
      };
      window[GOOGLE_MAPS_CALLBACK] = () => {
        clearLoader();
        if (window.google?.maps?.importLibrary) {
          resolve(window.google.maps);
        } else {
          reject(new Error("Google Maps loaded without the Maps JavaScript API."));
        }
      };
      script.src =
        "https://maps.googleapis.com/maps/api/js?key=" +
        encodeURIComponent(GOOGLE_MAPS_API_KEY) +
        "&v=weekly&loading=async&callback=" + GOOGLE_MAPS_CALLBACK;
      script.async = true;
      script.defer = true;
      script.dataset.quimeraMaps = "true";
      script.onerror = () => {
        clearLoader();
        reject(new Error("Unable to load Google Maps. Check the API key and allowed website referrers."));
      };
      timeoutId = window.setTimeout(() => {
        clearLoader();
        reject(new Error("Google Maps did not finish loading. Check the API key and enabled Maps APIs."));
      }, 20000);
      document.head.appendChild(script);
    }).catch((error) => {
      googleMapsScriptPromise = null;
      throw error;
    });
  }
  return googleMapsScriptPromise;
};
function SectionHeading({ number, title, description }) {
  return (
    <div className="quimera-create-section-heading">
      <span>{number}</span>
      <div>
        <h3>{sentenceCaseLabel(title)}</h3>
        <p>{description}</p>
      </div>
    </div>
  );
}

function AddressPanel({ title, value, onChange, details, onDetailsChange, required, side }) {
  const mapElementRef = useRef(null);
  const autocompleteHostRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const searchAddressRef = useRef(null);
  const [mapState, setMapState] = useState("loading");
  const [autocompleteReady, setAutocompleteReady] = useState(false);
  const [autocompleteElement, setAutocompleteElement] = useState(null);
  const [mapLoadError, setMapLoadError] = useState("");
  const [autocompleteError, setAutocompleteError] = useState("");

  onChangeRef.current = onChange;
  valueRef.current = value;

  useEffect(() => {
    const mapElement = mapElementRef.current;
    let active = true;
    let map;
    let marker;
    let geocoder;
    let autocomplete;
    let mapClickListener;
    let mapIdleListener;
    let markerDragListener;
    let autocompleteInputListener;
    let autocompleteSelectListener;
    let autocompleteErrorListener;

    const coordinatesFor = (location) => ({
      lat: typeof location.lat === "function" ? location.lat() : location.lat,
      lng: typeof location.lng === "function" ? location.lng() : location.lng,
    });

    const setMapLocation = (location, viewport) => {
      if (!map || !marker || !location) return;
      marker.position = location;
      if (viewport) {
        map.fitBounds(viewport);
      } else {
        map.panTo(location);
        map.setZoom(16);
      }
    };

    const chooseMapLocation = (location) => {
      if (!location) return;
      setMapLocation(location);
      const coordinates = coordinatesFor(location);
      const coordinateText = coordinates.lat.toFixed(6) + ", " + coordinates.lng.toFixed(6);
      if (!geocoder) {
        onChangeRef.current(coordinateText);
        return;
      }
      geocoder.geocode({ location }, (results, status) => {
        if (!active) return;
        const address =
          status === "OK" && results?.[0]?.formatted_address
            ? results[0].formatted_address
            : coordinateText;
        if (autocomplete) autocomplete.value = address;
        onChangeRef.current(address);
      });
    };

    const searchAddress = (address) => {
      const query = String(address || "").trim();
      if (!query) return;
      if (!geocoder) {
        setAutocompleteError("Address search is still loading. Please try again in a moment.");
        return;
      }
      geocoder.geocode({ address: query }, (results, status) => {
        if (!active) return;
        if (status !== "OK" || !results?.[0]?.geometry?.location) {
          setAutocompleteError("Address not found. Try a nearby street, city, or postal code.");
          return;
        }
        const result = results[0];
        const formattedAddress = result.formatted_address || query;
        if (autocomplete) autocomplete.value = formattedAddress;
        onChangeRef.current(formattedAddress);
        setMapLocation(result.geometry.location, result.geometry.viewport);
        setAutocompleteError("");
      });
    };
    searchAddressRef.current = searchAddress;

    const initialize = async () => {
      try {
        const mapsApi = await loadGoogleMapsApi();
        const [mapsLibrary, markerLibrary] = await Promise.all([
          mapsApi.importLibrary("maps"),
          mapsApi.importLibrary("marker"),
        ]);
        if (!active || !mapElement) return;

        const fallbackCenter = { lat: 20.5937, lng: 78.9629 };
        map = new mapsLibrary.Map(mapElement, {
          center: fallbackCenter,
          zoom: 4,
          mapId: "DEMO_MAP_ID",
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: "greedy",
        });
        marker = new markerLibrary.AdvancedMarkerElement({
          map,
          title: title + " location. Drag to adjust the selected point.",
          gmpDraggable: true,
        });
        markerDragListener = () => {
          if (marker?.position) chooseMapLocation(marker.position);
        };
        marker.addEventListener("gmp-dragend", markerDragListener);

        mapClickListener = map.addListener("click", (event) => {
          if (event.latLng) chooseMapLocation(event.latLng);
        });
        mapIdleListener = map.addListener("idle", () => {
          const bounds = map.getBounds();
          if (bounds && autocomplete) autocomplete.locationBias = bounds;
        });
        setMapState("ready");

        // Geocoding is optional for rendering the map. If it is disabled or
        // unavailable, users can still click the map or drag the pin to choose a point.
        mapsApi.importLibrary("geocoding").then((geocodingLibrary) => {
          if (!active) return;
          geocoder = new geocodingLibrary.Geocoder();
          const initialAddress = usableText(valueRef.current);
          if (initialAddress) {
            geocoder.geocode({ address: initialAddress }, (results, status) => {
              if (!active || status !== "OK" || !results?.[0]?.geometry) return;
              setMapLocation(
                results[0].geometry.location,
                results[0].geometry.viewport,
              );
            });
          }
        }).catch(() => {
          if (active) {
            setAutocompleteError("Address lookup is unavailable. Select a point directly on the map.");
          }
        });

        // Load suggestions separately so a Places API issue cannot prevent the map
        // and manual address search from working.
        mapsApi.importLibrary("places").then((placesLibrary) => {
          if (!active) return;
          autocomplete = new placesLibrary.PlaceAutocompleteElement({
            description: title + " address suggestions",
          });
          autocomplete.placeholder =
            side === "pickup" ? "Enter pickup address" : "Enter drop address";
          autocomplete.value = valueRef.current || "";
          autocomplete.noInputIcon = true;
          Object.assign(autocomplete.style, {
            width: "100%",
            height: "40px",
            display: "block",
            boxSizing: "border-box",
            border: "1px solid #d5e0ef",
            borderRadius: "11px",
            backgroundColor: "#fbfcfe",
            color: "#28374d",
            colorScheme: "light",
            fontFamily: "inherit",
            fontSize: "12px",
            fontWeight: "500",
            lineHeight: "18px",
          });
          setAutocompleteElement(autocomplete);
          const bounds = map.getBounds();
          if (bounds) autocomplete.locationBias = bounds;

          autocompleteInputListener = () => {
            onChangeRef.current(autocomplete.value || "");
          };
          autocompleteSelectListener = async (event) => {
            try {
              const place = event.placePrediction?.toPlace();
              if (!place) return;
              await place.fetchFields({
                fields: ["formattedAddress", "location", "viewport"],
              });
              if (!active || !place.location) return;
              const address = place.formattedAddress || place.displayName || "";
              autocomplete.value = address;
              onChangeRef.current(address);
              setMapLocation(place.location, place.viewport);
              setAutocompleteError("");
            } catch {
              if (active) {
                setAutocompleteError("Could not load that address. Please try another suggestion.");
              }
            }
          };
          autocompleteErrorListener = () => {
            if (active) {
              setAutocompleteError("Address suggestions are unavailable. You can still search manually or select a point on the map.");
            }
          };
          autocomplete.addEventListener("input", autocompleteInputListener);
          autocomplete.addEventListener("gmp-select", autocompleteSelectListener);
          autocomplete.addEventListener("gmp-error", autocompleteErrorListener);
          setAutocompleteReady(true);
        }).catch(() => {
          if (active) {
            setAutocompleteError("Address suggestions are unavailable. Search an address or select a point on the map.");
          }
        });
      } catch (error) {
        if (active) {
          setMapLoadError(error?.message || "Interactive map unavailable. Check your Google Maps configuration.");
          setMapState("error");
        }
      }
    };

    initialize();
    return () => {
      active = false;
      searchAddressRef.current = null;
      mapClickListener?.remove();
      mapIdleListener?.remove();
      marker?.removeEventListener("gmp-dragend", markerDragListener);
      autocomplete?.removeEventListener("input", autocompleteInputListener);
      autocomplete?.removeEventListener("gmp-select", autocompleteSelectListener);
      autocomplete?.removeEventListener("gmp-error", autocompleteErrorListener);
      if (marker) marker.map = null;
      autocompleteHostRef.current?.replaceChildren();
    };
  }, [side, title]);

  return (
    <div className="quimera-create-address-panel">
      <label className="quimera-create-field">
        <span>{title}{required && <b> *</b>}</span>
        <div className="quimera-create-map">
          {mapState !== "ready" && (
            <iframe
              title={title + " map"}
              src={mapEmbedUrl(value)}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          )}
          <div
            ref={mapElementRef}
            className="quimera-create-map-canvas"
            aria-label={title + " interactive map"}
          />
          {mapState === "loading" && (
            <span className="quimera-create-map-status" role="status">
              Loading interactive map...
            </span>
          )}
          {mapState === "error" && (
            <span className="quimera-create-map-status" role="status">
              {mapLoadError || "Interactive map unavailable. Check your Google Maps configuration."}
            </span>
          )}
        </div>
        <div className="quimera-create-address-input">
          {mapState === "ready" && autocompleteReady ? (
            <div
              ref={(node) => {
                autocompleteHostRef.current = node;
                if (node && autocompleteElement && !node.contains(autocompleteElement)) {
                  node.replaceChildren(autocompleteElement);
                }
              }}
              className="quimera-create-address-autocomplete"
            />
          ) : (
            <input
              value={value}
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  searchAddressRef.current?.(event.currentTarget.value);
                }
              }}
              placeholder={side === "pickup" ? "Enter pickup address" : "Enter drop address"}
              required={required}
            />
          )}
        </div>
        {autocompleteError && (
          <small className="quimera-create-map-error" role="status">
            {autocompleteError}
          </small>
        )}
      </label>
      {mapState === "ready" && (
        <button
          type="button"
          className="quimera-create-map-search-button"
          onClick={() => searchAddressRef.current?.(valueRef.current)}
        >
          Show location on map
        </button>
      )}
      <label className="quimera-create-field">
        <span>Address remaining details</span>
        <input value={details} onChange={onDetailsChange} placeholder="Enter address remaining details" />
      </label>
    </div>
  );
}

export default function CreateQuimeraOrderModal({ inquiry, onClose, onCreate }) {
  const initialCountry = useMemo(() => getCountry(inquiry), [inquiry]);
  const [form, setForm] = useState(() => initialForm(inquiry, initialCountry));
  const existingImage = usableText(inquiry?.image);
  const [productImage, setProductImage] = useState(existingImage === "/assets/inquiries-icon.svg" ? "" : existingImage);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedCountry = countryCodes.find((country) => country.code === form.countryCode) || initialCountry;
  const selectedLogisticsType = LOGISTICS_TYPES.find(
    (type) => type.value === form.logisticsType,
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isSubmitting, onClose]);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const quantityValue = Number(form.quantity);
  const weightValue = Number(form.weightKg);
  const dimensionsAreValid = [form.heightCm, form.widthCm].every(
    (value) =>
      value === "" ||
      (Number.isFinite(Number(value)) && Number(value) >= 0),
  );
  const isComplete = Boolean(
    form.logisticsType &&
    form.pickupAddress.trim() &&
    form.dropAddress.trim() &&
    /^\d+$/.test(form.phoneNumber) &&
    form.contactName.trim() &&
    form.pickupDate &&
    isValidEmailAddress(form.email) &&
    form.itemName.trim() &&
    Number.isFinite(quantityValue) &&
    quantityValue > 0 &&
    Number.isFinite(weightValue) &&
    weightValue > 0 &&
    dimensionsAreValid,
  );

  const handleProductImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const imageData = String(reader.result || "");
      setProductImage(imageData);
      setForm((current) => ({ ...current, productImage: imageData }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isComplete || !onCreate || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await onCreate({
        ...form,
        countryName: selectedCountry.name,
        phoneCountryCode: selectedCountry.code,
        phoneDialCode: selectedCountry.dial_code,
        fullPhoneNumber: selectedCountry.dial_code + form.phoneNumber.trim(),
      });
      onClose();
      toogleSuccessMessage(
        result?.message || "Quimera order created successfully.",
      );
    } catch (error) {
      toogleErrorMessage(
        error?.message || "Unable to create the Quimera order. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !isSubmitting) onClose();
  };

  const countryOptions = PHONE_COUNTRY_OPTIONS;

  return (
    <div className="quimera-create-overlay" onMouseDown={handleBackdropClick}>
      <section className="quimera-create-modal" role="dialog" aria-modal="true" aria-labelledby="quimera-create-title">
        <header className="quimera-create-header">
          <span className="quimera-create-header-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path d="m21 3-7.2 18-3.1-7.7L3 10.2 21 3Z" /><path d="m10.7 13.3 5-5" /></svg>
          </span>
          <div className="quimera-create-header-copy">
            <div className="quimera-create-title-row">
              <h2 id="quimera-create-title">Create Quimera order</h2>
              <span>{inquiry?.id || "Inquiry"}</span>
            </div>
            <p>Prepare shipment details for the selected inquiry.</p>
          </div>
          <button type="button" className="quimera-create-close" aria-label="Close create order modal" onClick={onClose} disabled={isSubmitting}>×</button>
        </header>

        <form className="quimera-create-form" onSubmit={handleSubmit}>
          <div className="quimera-create-body">
            <section className="quimera-create-card quimera-create-logistics-card">
              <div className="quimera-create-card-top">
                <div>
                  <h3 id="quimera-logistics-label">Logistics type <b>*</b></h3>
                  <p>Select the logistics channel for this Quimera order.</p>
                </div>
                <span
                  className={
                    "quimera-create-required" +
                    (selectedLogisticsType ? " is-selected" : "")
                  }
                  aria-live="polite"
                >
                  <i />
                  {selectedLogisticsType?.label || "Required"}
                </span>
              </div>
              <div
                className="quimera-create-logistics-options"
                role="radiogroup"
                aria-labelledby="quimera-logistics-label"
                aria-required="true"
              >
                {LOGISTICS_TYPES.map((type) => (
                  <label key={type.value} className={"quimera-create-logistics-option" + (form.logisticsType === type.value ? " selected" : "")}>
                    <input type="radio" name="quimera-logistics-type" value={type.value} checked={form.logisticsType === type.value} required onChange={() => update("logisticsType", type.value)} />
                    <span className="quimera-create-logistics-icon"><svg viewBox="0 0 24 24"><path d={type.icon} /></svg></span>
                    <strong>{type.label}</strong>
                  </label>
                ))}
              </div>
            </section>

            <section className="quimera-create-card">
              <SectionHeading number="01" title="Route details" description="Confirm pickup and drop locations before entering contact details." />
              <div className="quimera-create-route-grid">
                <AddressPanel title="Pickup address" side="pickup" value={form.pickupAddress} onChange={(address) => update("pickupAddress", address)} details={form.pickupAddressDetails} onDetailsChange={(event) => update("pickupAddressDetails", event.target.value)} required />
                <AddressPanel title="Drop address" side="drop" value={form.dropAddress} onChange={(address) => update("dropAddress", address)} details={form.dropAddressDetails} onDetailsChange={(event) => update("dropAddressDetails", event.target.value)} required />
              </div>
            </section>

            <section className="quimera-create-card">
              <SectionHeading number="02" title="Contact details" description="Use buyer contact information from the inquiry or update it for this booking." />
              <div className="quimera-create-contact-grid">
                <div className="quimera-create-field quimera-create-contact-phone">
                  <span id="quimera-phone-number-label">Phone number <b>*</b></span>
                  <div className="quimera-create-phone-input">
                    <CustomDropdown className="quimera-create-country-dropdown" ariaLabel="Select phone country code" value={form.countryCode} renderOptionLabel={renderPhoneCountryLabel} options={countryOptions} placeholder="Code" searchable searchPlaceholder="Search countries..." emptyMessage="No countries found" onChange={(value) => update("countryCode", value)} />
                    <input aria-labelledby="quimera-phone-number-label" value={form.phoneNumber} onChange={(event) => update("phoneNumber", sanitizeNumericInput(event.target.value, { allowDecimal: false }))} onKeyDown={(event) => handleNumericInputKeyDown(event, { allowDecimal: false })} placeholder="Phone number" inputMode="numeric" pattern="[0-9]*" required />
                  </div>
                </div>
                <label className="quimera-create-field quimera-create-contact-name">
                  <span>Name <b>*</b></span>
                  <input value={form.contactName} onChange={(event) => update("contactName", event.target.value)} placeholder="Contact name" required />
                </label>
                <label className="quimera-create-field quimera-create-contact-date">
                  <span>Pickup date <b>*</b></span>
                  <input type="date" value={form.pickupDate} onChange={(event) => update("pickupDate", event.target.value)} required />
                </label>
                <label className="quimera-create-field quimera-create-contact-email">
                  <span>Email <b>*</b></span>
                  <input type="email" value={form.email} onChange={(event) => update("email", event.target.value)} placeholder="Email address" required />
                </label>
              </div>
            </section>

            <section className="quimera-create-card">
              <SectionHeading number="03" title="Product attributes" description="Add the shipment item dimensions and product information." />
              <div className="quimera-create-product-grid">
                <label className="quimera-create-image-upload">
                  <input type="file" accept="image/*" onChange={handleProductImage} />
                  {productImage ? <img src={productImage} alt="Product preview" /> : <><svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg><span>Upload</span></>}
                </label>
                <div className="quimera-create-product-fields">
                  <label className="quimera-create-field quimera-create-item-name">
                    <span>Item name <b>*</b></span>
                    <input value={form.itemName} onChange={(event) => update("itemName", event.target.value)} placeholder="Enter item name" required />
                  </label>
                  <label className="quimera-create-field quimera-create-quantity">
                    <span>Quantity <b>*</b></span>
                    <input type="number" onKeyDown={handleNumericInputKeyDown} min="1" value={form.quantity} onChange={(event) => update("quantity", sanitizeNumericInput(event.target.value))} placeholder="Enter quantity" required />
                  </label>
                  <label className="quimera-create-field">
                    <span>Weight (kg) <b>*</b></span>
                    <input type="number" onKeyDown={handleNumericInputKeyDown} min="0.01" step="any" value={form.weightKg} onChange={(event) => update("weightKg", sanitizeNumericInput(event.target.value))} placeholder="Enter value" required />
                  </label>
                  <label className="quimera-create-field">
                    <span>Height (cm)</span>
                    <input type="number" onKeyDown={handleNumericInputKeyDown} min="0" step="any" value={form.heightCm} onChange={(event) => update("heightCm", sanitizeNumericInput(event.target.value))} placeholder="Enter value" />
                  </label>
                  <label className="quimera-create-field">
                    <span>Width (cm)</span>
                    <input type="number" onKeyDown={handleNumericInputKeyDown} min="0" step="any" value={form.widthCm} onChange={(event) => update("widthCm", sanitizeNumericInput(event.target.value))} placeholder="Enter value" />
                  </label>
                </div>
              </div>
            </section>

            <section className="quimera-create-card">
              <SectionHeading number="04" title="Detailed requirements" description="Enter item details such as color, size, materials, and any specification requirements." />
              <label className="quimera-create-field quimera-create-requirements">
                <textarea value={form.detailedRequirements} onChange={(event) => update("detailedRequirements", event.target.value)} placeholder="Enter item details such as color, size, materials, and any specification requirements." />
              </label>
            </section>
          </div>

          <footer className="quimera-create-footer">
            <span>Review all Quimera shipment details before creating the order.</span>
            <div>
              <button type="button" className="quimera-create-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
              <button type="submit" className="quimera-create-submit" disabled={!isComplete || !onCreate || isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Quimera order"}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}
